import { nanoid } from 'nanoid';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useDispatch, useSelector } from 'react-redux';

import {
  type DigitalQuote,
  type Order,
  useAccount,
  useContact,
  useDigitalQuote,
  useSupplierFacilitiesList,
} from '@frontend/api';
import {
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  Center,
  Spinner,
  VStack,
  DrawerOverline,
  DrawerButtonGroup,
  Button,
  Empty,
  EmptyDrawerIcon,
  EmptyHeadline,
} from '@frontend/components';

import { LeadTimeUnit } from 'constants/all';

import { GenericError } from 'modules/common/components';
import { featureFlagKeys } from 'modules/common/drawers/featureFlagDrawer/const/featureFlagKeys';

import { useAppSelector } from 'store/hooks';
import { getOrder } from 'store/orders/selectors';
import {
  getContact,
  getIsQuoteReadOnly,
  getQuote,
} from 'store/quotes/selectors';
import { useFeatureFlagValue } from 'store/session/hooks';

import { areSameAddresses } from 'utils/addressUtils';

import OldFacilitateOrderDrawer from './OldFacilitateOrderDrawer/FacilitateOrderDrawer';
import CustomerInformation from './components/CustomerInformation';
import { MultiplePartsHeader } from './components/MultiplePartsHeader';
import PartSelectionFooter from './components/PartSelectionFooter';
import PartSelector from './components/PartSelector';
import { ReviewOrder } from './components/ReviewOrder';
import { ReviewOrderFooter } from './components/ReviewOrderFooter';
import {
  BILLING_INFO_TYPES,
  DRAWER_MODES,
  SHIPPING_INFO_TYPES,
  SHIPPING_METHOD_RATES,
} from './constants';
import {
  facilitateOrderDrawer,
  receiveFacilitateOrderDrawerData,
  receiveInitialValues,
  setDrawerTab,
} from './data/actions';
import { useCompleteOrderV2, useCompleteRefacilitateOrder } from './data/hooks';
import {
  isFacilitateOrderDrawerOpen,
  getQuoteItems,
  getTab,
  getDrawerMode,
  getFilteredQuoteItems,
  getIsCustomerInfoValid,
  getIsCreatingOrder,
} from './data/reducer';
import { SelectedOption, ShippingType } from './data/types';

type BaseDrawerContentProps = {
  leadTimeDisplayUnit: LeadTimeUnit;
  isFetching: boolean;
  isCompletingOrder: boolean;
  quote: DigitalQuote;
  onCompleteOrder: () => void;
};

const BaseDrawerContent = ({
  leadTimeDisplayUnit,
  isFetching,
  isCompletingOrder,
  quote,
  onCompleteOrder,
}: BaseDrawerContentProps) => {
  const dispatch = useDispatch();
  const tab = useSelector(getTab);
  const quoteItems = useSelector(getQuoteItems);
  const filteredQuoteItems = useSelector(getFilteredQuoteItems);
  const drawerMode = useSelector(getDrawerMode);
  const isCustomerInfoValid = useSelector(getIsCustomerInfoValid);
  const drawerBodyRef = useRef<HTMLDivElement>(null);

  const handleSetDrawerTab = useCallback(
    (tab: string) => {
      dispatch(setDrawerTab({ tab }));
      if (drawerBodyRef.current) {
        drawerBodyRef.current.scrollTop = 0;
      }
    },
    [dispatch],
  );

  return (
    <DrawerContent>
      <DrawerHeader>
        <DrawerOverline>
          {drawerMode === DRAWER_MODES.EDIT ? 'Edit Order' : 'Build Order'}
        </DrawerOverline>
        {tab === 'part_selection'
          ? 'Build Order'
          : tab === 'customer_information'
          ? 'Shipping & Payment (Optional)'
          : 'Review'}
        <DrawerCloseButton />
      </DrawerHeader>
      {tab === 'part_selection' && quoteItems.length > 1 ? (
        <MultiplePartsHeader
          quoteItems={quoteItems}
          leadTimeDisplayUnit={leadTimeDisplayUnit}
        />
      ) : null}
      <DrawerBody ref={drawerBodyRef} {...(tab === 'review' && { padding: 0 })}>
        {isFetching || quoteItems.length === 0 ? (
          <Center height="100%">
            <Spinner size="xl" />
          </Center>
        ) : tab === 'part_selection' ? (
          <VStack height="100%" width="100%" spacing={2}>
            {filteredQuoteItems.map((quoteItem) => (
              <PartSelector
                key={`part-selector-${quoteItem.id}`}
                quoteItem={quoteItem}
                leadTimeDisplayUnit={leadTimeDisplayUnit}
              />
            ))}
            {filteredQuoteItems.length === 0 ? (
              <Empty mt={3.5}>
                <EmptyDrawerIcon />
                <EmptyHeadline>No matching line items</EmptyHeadline>
              </Empty>
            ) : null}
          </VStack>
        ) : tab === 'customer_information' ? (
          <CustomerInformation />
        ) : (
          <ReviewOrder quote={quote} />
        )}
      </DrawerBody>
      <DrawerFooter>
        {tab === 'part_selection' ? (
          <PartSelectionFooter />
        ) : tab === 'customer_information' ? (
          <DrawerButtonGroup>
            <Button
              variant="secondary"
              onClick={() => handleSetDrawerTab('part_selection')}
            >
              Go Back
            </Button>
            <Button
              onClick={() => handleSetDrawerTab('review')}
              isDisabled={!isCustomerInfoValid}
            >
              Review Order
            </Button>
          </DrawerButtonGroup>
        ) : (
          <ReviewOrderFooter
            isCompletingOrder={isCompletingOrder}
            handleSetDrawerTab={handleSetDrawerTab}
            onCompleteOrder={onCompleteOrder}
          />
        )}
      </DrawerFooter>
    </DrawerContent>
  );
};

const useInitFacilitateOrderDrawer = ({ isOpen }: { isOpen: boolean }) => {
  const dispatch = useDispatch();
  const quote = useSelector(getQuote);
  const quoteContact = useSelector(getContact);
  const isQuoteReadOnly = useSelector(getIsQuoteReadOnly);
  const maxSupplierFacilities = useFeatureFlagValue(
    featureFlagKeys.maxSupplierFacilities,
  );

  const supplierFacilityId = quote?.supplierFacility?.id;
  const digiQuoteKey = quote?.digiQuoteKey;

  const { data: contact, isFetching: isFetchingContact } = useContact(
    { contactId: quoteContact?.id },
    { queryOptions: { enabled: isOpen } },
  );
  const { data: account, isFetching: isFetchingAccount } = useAccount(
    { accountId: contact?.account?.id },
    { queryOptions: { enabled: !!contact?.account && isOpen } },
  );
  const { data: supplierFacilities, isFetching: isFetchingSupplierFacilities } =
    useSupplierFacilitiesList({
      queryOptions: { enabled: isOpen },
    });
  const { data: quoteResponse, isFetching: isFetchingQuoteResponse } =
    useDigitalQuote(
      { digiQuoteKey: digiQuoteKey ?? '', isPreview: false },
      { queryOptions: { enabled: isOpen && !!digiQuoteKey, staleTime: 0 } },
    );
  const quoteItems = useMemo(() => {
    if (quoteResponse?.quoteItems) {
      return quoteResponse.quoteItems.map((quoteItem) => ({
        ...quoteItem,
        uuid: nanoid(),
      }));
    }
    return [];
  }, [quoteResponse]);
  // Only dispatch when all data is loaded and drawer is open
  useEffect(() => {
    if (
      isOpen &&
      contact &&
      (account || !contact.account) &&
      supplierFacilities &&
      quoteItems &&
      supplierFacilityId
    ) {
      dispatch(
        receiveFacilitateOrderDrawerData({
          account,
          contact,
          isQuoteReadOnly,
          maxSupplierFacilities,
          quoteItems,
          supplierFacilities,
          supplierFacilityId,
          withConfirmation:
            quoteResponse?.displaySettings?.sendOrderConfirmationEmails ??
            false,
        }),
      );
    }
  }, [
    isOpen,
    contact,
    account,
    supplierFacilities,
    quoteItems,
    supplierFacilityId,
    dispatch,
    isQuoteReadOnly,
    maxSupplierFacilities,
    quoteResponse?.displaySettings?.sendOrderConfirmationEmails,
  ]);

  return {
    leadTimeDisplayUnit: quote?.leadTimeDisplayUnit,
    isFetching:
      isFetchingContact ||
      isFetchingAccount ||
      isFetchingSupplierFacilities ||
      isFetchingQuoteResponse,
    quote: quoteResponse,
  };
};

const FacilitateNewOrderDrawerContent = () => {
  const isOpen = useSelector(isFacilitateOrderDrawerOpen);
  const { leadTimeDisplayUnit, isFetching, quote } =
    useInitFacilitateOrderDrawer({
      isOpen,
    });
  const completeOrder = useCompleteOrderV2();
  const isCompletingOrder = useAppSelector(getIsCreatingOrder);

  return (
    <BaseDrawerContent
      leadTimeDisplayUnit={leadTimeDisplayUnit}
      isFetching={isFetching}
      isCompletingOrder={isCompletingOrder}
      quote={quote}
      onCompleteOrder={completeOrder}
    />
  );
};

const useInitRefacilitateOrderDrawer = () => {
  const dispatch = useDispatch();
  const isOpen = useSelector(isFacilitateOrderDrawerOpen);
  const maxSupplierFacilities = useFeatureFlagValue(
    featureFlagKeys.maxSupplierFacilities,
  );
  const order: Order | undefined = useSelector(getOrder);
  const digiQuoteKey = order?.digiQuoteKey;

  const { data: digiQuoteData, isFetching: isFetchingDigiQuoteData } =
    useDigitalQuote(
      { digiQuoteKey, isPreview: true },
      {
        queryOptions: { enabled: isOpen && !!digiQuoteKey },
      },
    );

  const { data: supplierFacilities, isFetching: isFetchingSupplierFacilities } =
    useSupplierFacilitiesList({
      queryOptions: { enabled: isOpen },
    });

  const editOrderData = useMemo(() => {
    if (digiQuoteData && supplierFacilities) {
      const { account, contact, supplierFacility, quoteItems } = digiQuoteData;

      return {
        account,
        contact,
        maxSupplierFacilities,
        quoteItems,
        supplierFacilities,
        supplierFacilityId: supplierFacility.id,
      };
    } else {
      return null;
    }
  }, [digiQuoteData, supplierFacilities, maxSupplierFacilities]);

  const initialValues = useMemo(() => {
    if (order && digiQuoteData) {
      const {
        billingInformation,
        confirmationEmail,
        paymentMethod,
        pointOfContact,
        purchaseToken: poNumber,
        orderItems,
        sendCustomerConfirmation,
        shippingInformation,
        shippingOption,
      } = order;
      const { account, quoteItems } = digiQuoteData;

      const selectedOptions: Record<number, SelectedOption> = {};
      const adjustedLeadTimes: Record<number, number | null> = {};
      const adjustedShipsOn: Record<number, string | null> = {};
      orderItems.forEach((orderItem) => {
        const {
          quoteItem: { id: quoteItemId },
          quantityId,
          expediteId,
          orderedAddOns,
          leadTime,
          shipsOn,
        } = orderItem;
        const quoteItem = quoteItems.find(
          (quoteItem) => quoteItem.id === quoteItemId,
        );
        if (quoteItem) {
          const { quantities, addOnCells } = quoteItem.rootComponent;
          const componentQuantity = quantities.find(
            (quantity) => quantity.id === quantityId,
          );
          if (componentQuantity) {
            const expedite = componentQuantity.expedites.find(
              (expedite) => expedite.id === expediteId,
            );
            const idToUse = expedite ? expediteId : quantityId;
            adjustedLeadTimes[idToUse] = leadTime;
            adjustedShipsOn[idToUse] = shipsOn;
            const addOns = {};
            orderedAddOns.forEach((addOn) => {
              const digiQuoteAddOnCell = addOnCells.find(
                (addOnCell) =>
                  addOnCell.addOnId === addOn.id &&
                  addOnCell.quantityId === addOn.quantityId,
              );
              addOns[addOn.id] = digiQuoteAddOnCell;
            });
            selectedOptions[quoteItem.id] = {
              quantity: componentQuantity,
              expedite,
              addOns,
              manualLeadTime: leadTime,
              shipsOn,
            };
          }
        }
      });

      const facility =
        shippingInformation && account
          ? account.facilities.find(
              (facility) =>
                facility.name === shippingInformation.facilityName &&
                areSameAddresses(facility.address, shippingInformation.address),
            )
          : undefined;

      const shippingInformationType = facility
        ? SHIPPING_INFO_TYPES.FACILITY
        : shippingInformation
        ? SHIPPING_INFO_TYPES.ONE_TIME_ADDRESS
        : SHIPPING_INFO_TYPES.NONE;

      const billingAddress =
        billingInformation && account
          ? account.billingAddresses.find((billingAddress) =>
              areSameAddresses(
                billingAddress.address,
                billingInformation.address,
              ),
            )
          : undefined;

      const billingInformationType = billingAddress
        ? BILLING_INFO_TYPES.BILLING_ADDRESS
        : billingInformation
        ? BILLING_INFO_TYPES.ONE_TIME_ADDRESS
        : BILLING_INFO_TYPES.NONE;

      const useShippingAddress =
        !!billingInformation &&
        !!shippingInformation &&
        areSameAddresses(
          billingInformation.address,
          shippingInformation.address,
        );

      let shippingType: ShippingType | undefined;
      let shippingAccount: any | undefined;
      if (shippingOption) {
        const { type, ...rest } = shippingOption;
        shippingType = type;
        shippingAccount = rest;
      }
      const shippingRate = shippingAccount
        ? SHIPPING_METHOD_RATES.find(
            (rate) => rate.method === shippingAccount.shippingMethod,
          )
        : undefined;

      return {
        adjustedLeadTimes,
        adjustedShipsOn,
        billingAddress,
        billingInformation,
        billingInformationType,
        confirmationEmail,
        facility,
        paymentMethod,
        pointOfContact,
        purchaseOrderNumber: poNumber ?? undefined,
        selectedOptions,
        shippingAccount,
        shippingInformation,
        shippingInformationType,
        shippingRate,
        shippingType,
        useShippingAddress,
        withConfirmation: sendCustomerConfirmation,
      };
    } else {
      return null;
    }
  }, [order, digiQuoteData]);

  useEffect(() => {
    if (isOpen && editOrderData && initialValues) {
      dispatch(
        receiveFacilitateOrderDrawerData({
          ...editOrderData,
          isQuoteReadOnly: true,
        }),
      );
      dispatch(receiveInitialValues(initialValues));
    }
  }, [isOpen, dispatch, editOrderData, initialValues]);

  return {
    quote: digiQuoteData,
    leadTimeDisplayUnit: digiQuoteData?.leadTimeDisplayUnit,
    isFetching: isFetchingDigiQuoteData || isFetchingSupplierFacilities,
  };
};

const RefacilitateOrderDrawerContent = () => {
  const { leadTimeDisplayUnit, isFetching, quote } =
    useInitRefacilitateOrderDrawer();
  const { refacilitateOrder, isLoading } = useCompleteRefacilitateOrder();

  return (
    <BaseDrawerContent
      leadTimeDisplayUnit={leadTimeDisplayUnit}
      isFetching={isFetching}
      isCompletingOrder={isLoading}
      quote={quote}
      onCompleteOrder={refacilitateOrder}
    />
  );
};

const FacilitateOrderDrawer = () => {
  const dispatch = useDispatch();
  const isOpen = useSelector(isFacilitateOrderDrawerOpen);
  const drawerMode = useSelector(getDrawerMode);
  const close = useCallback(() => {
    dispatch(facilitateOrderDrawer.close());
  }, [dispatch]);

  return (
    <Drawer isOpen={isOpen} onClose={close} size="lg">
      <ErrorBoundary
        fallback={
          <DrawerContent>
            <DrawerCloseButton />
            <DrawerBody>
              <GenericError size="lg" height="100%" justifyContent="center" />
            </DrawerBody>
          </DrawerContent>
        }
        onError={(error) =>
          console.error('Error occurred in FacilitateOrderDrawer', error)
        }
      >
        {drawerMode === DRAWER_MODES.EDIT ? (
          <RefacilitateOrderDrawerContent />
        ) : (
          <FacilitateNewOrderDrawerContent />
        )}
      </ErrorBoundary>
    </Drawer>
  );
};

const FeatureFlagFacilitateOrderDrawer = () => {
  const isNewFacilitateOrderDrawerEnabled = useFeatureFlagValue(
    featureFlagKeys.isNewFacilitateOrderDrawerEnabled,
  );
  return isNewFacilitateOrderDrawerEnabled ? (
    <FacilitateOrderDrawer />
  ) : (
    <OldFacilitateOrderDrawer />
  );
};

export default FeatureFlagFacilitateOrderDrawer;
